import { useBasket } from '@/app/[locale]/(basket)/index'
import { useDeliveryAddress } from '@/stores/useAddressStore'
import { useCoupon } from '@/stores/useGlobalStore'
import { isAddressInDeliveryArea, isFreightProduct } from '@/lib/freight'
import { formatAmountToNOK } from '@/lib/utils'
import { useTranslations } from 'next-intl'

export const BasketPriceSummary = () => {
  const basket = useBasket()
  const deliveryAddress = useDeliveryAddress()
  const coupon = useCoupon()
  const t = useTranslations()

  // Hide prices for company baskets
  if (basket?.isCompany) {
    return null
  }

  if (basket?.cart.length === 0) {
    return null
  }

  const isValidAddress = deliveryAddress
    ? isAddressInDeliveryArea(deliveryAddress.kommunenummer, deliveryAddress.postnummer)
    : false

  // Check if freight items have null prices when basket is ready
  const freightItemsWithNullPrice = basket?.cart?.filter(
    (item) => isFreightProduct(item) && (!item.price?.gross || item.price?.gross === null),
  )
  const hasFreightWithNullPrices = freightItemsWithNullPrice && freightItemsWithNullPrice.length > 0

  // Show retry button if: valid address AND (freight has null prices OR server failed)
  if (
    isValidAddress &&
    ((basket?.status === 'ready' && hasFreightWithNullPrices) || basket?.status === 'server-update-failed')
  ) {
    return (
      <div className="flex w-full flex-col gap-1">
        <div className="flex w-full items-center justify-between">
          <div className="font-bold text-red-500">{t('Failed_to_load_freight_prices')}</div>
          <button
            onClick={() => basket?.actions.retryServerUpdate()}
            className="cursor-pointer rounded border border-current bg-transparent px-2 py-1"
          >
            {t('Retry')}
          </button>
        </div>
      </div>
    )
  }
  const shouldExcludeFreight = !deliveryAddress || !isValidAddress

  let freightTotal = 0
  if (shouldExcludeFreight) {
    const freightItems = basket?.cart?.filter((item) => isFreightProduct(item))
    freightTotal = freightItems.reduce((sum, item) => sum + item.quantity * (item.price?.gross ?? 0), 0)
  }

  const hasActiveVoucher = coupon && coupon.trim() !== ''
  const hasDiscount = basket?.total?.discount > 0 && hasActiveVoucher
  const isLoading = basket?.status === 'server-basket-is-stale'

  const adjustedGross = shouldExcludeFreight ? (basket?.total?.gross ?? 0) - freightTotal : (basket?.total?.gross ?? 0)

  const data = {
    initialPrice: {
      label: t('Initial_price'),
      value: adjustedGross + basket?.total?.discount,
    },
    discount: {
      label: `${t('Discount')} ${(basket?.total?.discount * 100) / (adjustedGross + basket?.total?.discount)}%`,
      value: basket?.total?.discount * -1,
    },
    total: {
      label: t('Total'),
      value: adjustedGross,
    },
  }

  return (
    <div className="flex w-full flex-col gap-1">
      {Object.entries(data).map(([key, { label, value }]) => {
        if (key !== 'total' && !hasDiscount) return null

        return (
          <div key={key} className="flex w-full justify-between font-bold">
            <div>{label}</div>
            {isLoading ? <div className="animate-pulse">...</div> : <div>{formatAmountToNOK(value)}</div>}
          </div>
        )
      })}
    </div>
  )
}
